import React, { useEffect, useRef, useState } from "react";
import { api, authHeaders, getToken } from "@/lib/api";

const MILESTONES = [10, 25, 50, 100, 150, 200, 300, 500, 750, 1000];
function nextGoal(total) {
  let prev = 0;
  for (const m of MILESTONES) { if (m > total) return { goal: m, prev }; prev = m; }
  const goal = Math.ceil((total + 1) / 500) * 500;
  return { goal, prev: goal - 500 };
}

// Smooth count-up from the previous value to the new target.
function useCountUp(target, duration = 1100) {
  const [val, setVal] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    let raf;
    const from = prev.current;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(from + (target - from) * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
      else prev.current = target;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
}

const fmt = (n) => Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function SavingsHero() {
  const [data, setData] = useState({ total_saved: 0, operations: 0 });

  const load = async () => {
    if (!getToken("client")) return;
    try {
      const r = await api.get("/client/history", { headers: authHeaders("client") });
      setData({ total_saved: r.data.total_saved || 0, operations: r.data.operations || 0 });
    } catch (_) {}
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const on = () => load();
    window.addEventListener("kn-coupons-changed", on);
    return () => window.removeEventListener("kn-coupons-changed", on);
  }, []);

  const total = data.total_saved;
  const animated = useCountUp(total);
  const empty = total <= 0;
  const { goal, prev } = nextGoal(total);
  const pct = empty ? 0 : Math.max(4, Math.min(100, Math.round(((total - prev) / (goal - prev)) * 100)));
  const missing = Math.max(0, goal - total);

  const whole = Math.floor(animated);
  const cents = Math.round((animated - whole) * 100);
  const wholeStr = whole.toLocaleString("it-IT");
  const centsStr = String(Math.min(99, cents)).padStart(2, "0");

  return (
    <div data-testid="savings-hero" className="kn-savings relative overflow-hidden rounded-3xl px-5 py-4 kn-fade-up text-center">
      <div className="pointer-events-none absolute -top-10 -right-10 h-28 w-28 rounded-full border border-white/20" />
      <div className="pointer-events-none absolute -bottom-12 -left-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

      <div className="relative">
        <p className="kn-savings-amt text-[11px] font-extrabold uppercase tracking-[0.18em] text-white/85">
          Hai risparmiato
        </p>

        <div className="kn-savings-amt mt-0.5 flex items-end justify-center gap-1 text-white">
          <span data-testid="savings-hero-total" className="text-5xl sm:text-6xl font-black leading-none tabular-nums tracking-tight">
            {wholeStr}
          </span>
          <span className="text-xl font-black text-white/70 mb-1 tabular-nums">,{centsStr}</span>
          <span className="text-2xl font-black mb-0.5 ml-0.5">€</span>
        </div>

        {empty ? (
          <p className="mt-2.5 text-[13px] font-semibold text-white/85">
            Usa il tuo primo coupon in cassa e inizia a risparmiare
          </p>
        ) : (
          <div className="mt-3">
            <div className="h-2.5 w-full rounded-full bg-black/15 overflow-hidden ring-1 ring-white/20">
              <div className="kn-savings-fill h-full rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <p className="mt-2 text-[12px] font-semibold text-white/90">
              Ti mancano <b className="text-white">€{fmt(missing)}</b> per il traguardo di €{fmt(goal)}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
